import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { MaintenanceIssueCard } from "@/components/maintenance/MaintenanceIssueCard";
import { MaintenanceHistoryItem } from "@/components/maintenance/MaintenanceHistoryItem";
import { ReportIssueForm } from "@/components/maintenance/ReportIssueForm";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const MaintenancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  console.log("Current user in maintenance page:", user);

  React.useEffect(() => {
    if (user && user.role !== "admin") {
      console.log("User is not admin, redirecting to homepage");
      navigate("/");
    }
  }, [user, navigate]);

  const {
    data: issues,
    isLoading: issuesLoading,
    refetch: refetchIssues
  } = useQuery({
    queryKey: ["maintenance-issues"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance_issues")
        .select(`
          *,
          bikes (
            serial_number,
            status,
            model_id
          ),
          reported_by (
            username
          )
        `)
        .order("reported_at", { ascending: false });

      if (error) {
        console.error("Error fetching maintenance issues:", error);
        toast.error("Failed to load maintenance issues");
        throw error;
      }
      
      console.log("Fetched maintenance issues:", data);
      return data || [];
    },
  });

  const {
    data: history,
    isLoading: historyLoading,
    refetch: refetchHistory
  } = useQuery({
    queryKey: ["maintenance-history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance_issues_history")
        .select(`
          *,
          bike:bikes (
            serial_number
          ),
          changed_by:users (
            username
          )
        `)
        .order("change_timestamp", { ascending: false });

      if (error) {
        console.error("Error fetching maintenance history:", error);
        toast.error("Failed to load maintenance history");
        throw error;
      }
      
      console.log("Fetched maintenance history:", data);
      return data || [];
    },
  });

  const handleUpdate = () => {
    refetchIssues();
    refetchHistory();
    // Removed toast notification here
  };

  if (issuesLoading || historyLoading) {
    return <div className="p-4">Loading maintenance data...</div>;
  }

  const activeIssues = issues?.filter(issue => issue.status !== "resolved") || [];
  const resolvedIssues = issues?.filter(issue => issue.status === "resolved") || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Maintenance</h1>
        <Button 
          onClick={() => {
            refetchIssues();
            refetchHistory();
            toast.success("Maintenance data refreshed");
          }} 
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="mb-6 w-full bg-gray-100 p-0 h-auto">
          <TabsTrigger 
            value="report" 
            className="py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-none rounded-none"
          >
            Report Issue
          </TabsTrigger>
          <TabsTrigger 
            value="active" 
            className="py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-none rounded-none"
          >
            Active Issues
            {activeIssues.length > 0 && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {activeIssues.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="resolved" 
            className="py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-none rounded-none"
          >
            Resolved Issues
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-none rounded-none"
          >
            Maintenance History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="report" className="space-y-4 mt-0">
          <div className="p-4 pb-6">
            <ReportIssueForm onSuccess={handleUpdate} />
          </div>
        </TabsContent>

        <TabsContent value="active" className="space-y-4 mt-0">
          <div>
            {activeIssues.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">No active maintenance issues found.</div>
            ) : (
              activeIssues.map((issue) => (
                <MaintenanceIssueCard
                  key={issue.id}
                  issue={issue}
                  onUpdate={handleUpdate}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="resolved" className="space-y-4 mt-0">
          <div>
            {resolvedIssues.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">No resolved maintenance issues found.</div>
            ) : (
              resolvedIssues.map((issue) => (
                <MaintenanceIssueCard
                  key={issue.id}
                  issue={issue}
                  onUpdate={handleUpdate}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-0">
          <div>
            {!history || history.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">No maintenance history found.</div>
            ) : (
              history.map((item) => (
                <MaintenanceHistoryItem
                  key={item.id}
                  item={item}
                  onUpdate={handleUpdate}
                />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MaintenancePage;
